<template>
  <div  class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            Disease Case Details
            <span v-if="isReportedToWoreda" class="badge bg-success ms-2">
              <i class="bx bx-check"></i> Reported to Woreda
            </span>
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Alert if notifiable but not reported -->
          <div v-if="isNotifiable && !isReportedToWoreda"
               class="alert alert-danger alert-dismissible fade show" role="alert">
            <h5 class="alert-heading">
              <i class="bx bx-error-circle me-2"></i>Immediate Action Required!
            </h5>
            <p class="mb-0">
              This is a notifiable disease that must be reported to Woreda Health Office immediately.
            </p>
            <hr>
            <div class="d-flex justify-content-end">
              <button @click="reportToWoreda" class="btn btn-danger btn-sm" :disabled="reporting">
                <span v-if="reporting">
                  <span class="spinner-border spinner-border-sm me-1"></span> Reporting...
                </span>
                <span v-else>
                  <i class="bx bx-send me-1"></i> Report to Woreda Now
                </span>
              </button>
            </div>
          </div>

          <!-- Case Information -->
          <div class="row">
            <div class="col-md-6">
              <div class="card border">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Case Information</h6>
                </div>
                <div class="card-body">
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Case ID:</div>
                    <div class="col-7 fw-bold">#{{ caseData.surveillance_id }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Disease:</div>
                    <div class="col-7">
                      <span class="fw-bold">{{ caseData.disease_type }}</span>
                      <span v-if="isNotifiable" class="badge bg-danger ms-2">Notifiable</span>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Disease Code:</div>
                    <div class="col-7">{{ caseData.disease_code || 'Not specified' }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Onset Date:</div>
                    <div class="col-7">
                      {{ formatDate(caseData.onset_date) }}
                      <small class="text-muted">({{ getDaysAgo(caseData.onset_date) }} days ago)</small>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Reported Date:</div>
                    <div class="col-7">{{ formatDateTime(caseData.reported_date) }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Severity:</div>
                    <div class="col-7">
                      <span :class="getSeverityBadge(caseData.severity)">
                        {{ caseData.severity }}
                      </span>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Reported By:</div>
                    <div class="col-7">{{ caseData.reported_by_name || 'Unknown' }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="card border">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Patient Information</h6>
                </div>
                <div class="card-body">
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Name:</div>
                    <div class="col-7 fw-bold">
                      {{ patientFullName }}
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Age/Gender:</div>
                    <div class="col-7">
                      {{ patientAge }} / {{ caseData.gender }}
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">CBHI Number:</div>
                    <div class="col-7">
                      {{ caseData.cbhi_number || 'Not enrolled' }}
                      <span v-if="caseData.cbhi_number" class="badge bg-success ms-2">Active</span>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Household:</div>
                    <div class="col-7">{{ caseData.household_number }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Phone:</div>
                    <div class="col-7">{{ caseData.phone_number || 'Not available' }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-5 text-muted">Address:</div>
                    <div class="col-7">
                      {{ patientAddress }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Clinical Information -->
          <div class="card border mt-3">
            <div class="card-header bg-light">
              <h6 class="mb-0">Clinical Information</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <h6 class="text-muted mb-2">Symptoms</h6>
                  <p>{{ caseData.symptoms || 'No symptoms recorded' }}</p>
                </div>
                <div class="col-md-6">
                  <h6 class="text-muted mb-2">Treatment Given</h6>
                  <p>{{ caseData.treatment_given || 'No treatment recorded' }}</p>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-md-4">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" :checked="caseData.isolation_required" disabled>
                    <label class="form-check-label">
                      Isolation Required
                    </label>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" :checked="caseData.hospitalized" disabled>
                    <label class="form-check-label">
                      Hospitalized
                    </label>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="text-muted">Contacts Traced:</label>
                  <span class="fw-bold ms-2">{{ caseData.contacts_traced || 0 }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Contact Tracing -->
          <div v-if="caseData.contacts_traced > 0" class="card border mt-3">
            <div class="card-header bg-light d-flex justify-content-between align-items-center">
              <h6 class="mb-0">Contact Tracing Information</h6>
              <button @click="showContactForm = !showContactForm" class="btn btn-sm btn-outline-primary">
                <i class="bx bx-plus"></i> Add Contact
              </button>
            </div>
            <div class="card-body">
              <div v-if="showContactForm" class="mb-3 p-3 border rounded bg-light">
                <h6 class="mb-3">Add New Contact</h6>
                <div class="row">
                  <div class="col-md-6 mb-2">
                    <input v-model="newContact.name" type="text" class="form-control"
                           placeholder="Contact Name">
                  </div>
                  <div class="col-md-6 mb-2">
                    <input v-model="newContact.phone" type="text" class="form-control"
                           placeholder="Phone Number">
                  </div>
                  <div class="col-md-6 mb-2">
                    <select v-model="newContact.relationship" class="form-select">
                      <option value="">Relationship</option>
                      <option value="family">Family Member</option>
                      <option value="neighbor">Neighbor</option>
                      <option value="coworker">Co-worker</option>
                      <option value="friend">Friend</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div class="col-md-6 mb-2">
                    <input v-model="newContact.lastExposure" type="date" class="form-control"
                           placeholder="Last Exposure Date">
                  </div>
                  <div class="col-12">
                    <button @click="addContact" class="btn btn-primary btn-sm me-2">
                      <i class="bx bx-check"></i> Add
                    </button>
                    <button @click="showContactForm = false" class="btn btn-secondary btn-sm">
                      <i class="bx bx-x"></i> Cancel
                    </button>
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-sm">
                  <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Relationship</th>
                    <th>Last Exposure</th>
                    <th>Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(contact, index) in contacts" :key="index">
                    <td>{{ contact.name }}</td>
                    <td>{{ contact.phone }}</td>
                    <td>{{ contact.relationship }}</td>
                    <td>{{ formatDate(contact.lastExposure) }}</td>
                    <td>
                      <span class="badge bg-warning">Monitoring</span>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Action History -->
          <div class="card border mt-3">
            <div class="card-header bg-light">
              <h6 class="mb-0">Action History</h6>
            </div>
            <div class="card-body">
              <div class="timeline">
                <div v-for="(action, index) in actionHistory" :key="index" class="timeline-item">
                  <div class="timeline-point" :class="getTimelineClass(action.type)"></div>
                  <div class="timeline-content">
                    <h6 class="mb-1">{{ action.title }}</h6>
                    <p class="mb-0 text-muted">{{ action.description }}</p>
                    <small class="text-muted">
                      <i class="bx bx-time-five"></i> {{ formatDateTime(action.date) }}
                      <span v-if="action.by"> • By {{ action.by }}</span>
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional Notes -->
          <div class="card border mt-3">
            <div class="card-header bg-light">
              <h6 class="mb-0">Additional Notes</h6>
            </div>
            <div class="card-body">
              <textarea v-model="additionalNotes" class="form-control" rows="3"
                        placeholder="Add any additional notes or observations..."></textarea>
              <button @click="saveNotes" class="btn btn-primary btn-sm mt-2" :disabled="savingNotes">
                <span v-if="savingNotes">
                  <span class="spinner-border spinner-border-sm me-1"></span> Saving...
                </span>
                <span v-else>
                  <i class="bx bx-save"></i> Save Notes
                </span>
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Close</button>

          <button v-if="!isReportedToWoreda && isNotifiable"
                  @click="reportToWoreda" type="button" class="btn btn-danger" :disabled="reporting">
            <i class="bx bx-send me-1"></i> Report to Woreda
          </button>

          <button @click="updateCase" type="button" class="btn btn-warning">
            <i class="bx bx-edit me-1"></i> Update Case
          </button>

          <button @click="printCase" type="button" class="btn btn-primary">
            <i class="bx bx-printer me-1"></i> Print
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "CaseDetailsModal",
  props: {
    case: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      reporting: false,
      savingNotes: false,

      // Contact tracing
      showContactForm: false,
      newContact: {
        name: '',
        phone: '',
        relationship: '',
        lastExposure: ''
      },
      contacts: [],

      // Additional notes
      additionalNotes: '',

      // Action history
      actionHistory: [],

      // Notifiable diseases list
      notifiableDiseases: ['AFP', 'Measles', 'Cholera', 'Meningitis', 'Yellow Fever', 'COVID-19', 'Polio', 'Rabies']
    };
  },

  computed: {
    caseData() {
      return this.case || {};
    },

    isNotifiable() {
      return this.caseData.disease_type && this.notifiableDiseases.includes(this.caseData.disease_type);
    },

    isReportedToWoreda() {
      return this.caseData.reported_to_woreda === true;
    },

    patientFullName() {
      const firstName = this.caseData.first_name || '';
      const surname = this.caseData.surname || '';
      return `${firstName} ${surname}`.trim() || 'Unknown';
    },

    patientAge() {
      return this.calculateAge(this.caseData.date_of_birth);
    },

    patientAddress() {
      const kebele = this.caseData.kebele_name || '';
      const woreda = this.caseData.woreda_name || '';
      if (kebele && woreda) {
        return `${kebele}, ${woreda}`;
      }
      return kebele || woreda || 'Unknown';
    }
  },

  mounted() {
    this.loadCaseDetails();
    this.buildActionHistory();
  },

  methods: {
    async loadCaseDetails() {
      // Load additional case details like contacts and notes
      // This would normally come from API
      this.contacts = [
        {
          name: 'John Doe',
          phone: '+251911234567',
          relationship: 'Family Member',
          lastExposure: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
        }
      ];

      this.additionalNotes = this.caseData.additional_notes || '';
    },

    buildActionHistory() {
      this.actionHistory = [
        {
          type: 'created',
          title: 'Case Reported',
          description: `Disease case reported for ${this.caseData.disease_type || 'Unknown'}`,
          date: this.caseData.reported_date,
          by: this.caseData.reported_by_name
        }
      ];

      if (this.caseData.treatment_given) {
        this.actionHistory.push({
          type: 'treatment',
          title: 'Treatment Provided',
          description: this.caseData.treatment_given,
          date: this.caseData.treatment_date || this.caseData.reported_date,
          by: this.caseData.reported_by_name
        });
      }

      if (this.caseData.reported_to_woreda) {
        this.actionHistory.push({
          type: 'reported',
          title: 'Reported to Woreda',
          description: 'Case reported to Woreda Health Office',
          date: this.caseData.woreda_report_date || this.caseData.reported_date,
          by: this.caseData.reported_by_name
        });
      }

      // Sort by date descending
      this.actionHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
    },

    async reportToWoreda() {
      if (!confirm(`Report ${this.caseData.disease_type} case to Woreda Health Office?`)) return;

      this.reporting = true;

      const data = {
        "function": "reportDiseaseToWoreda",
        "surveillanceId": this.caseData.surveillance_id
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          alert('Case reported to Woreda successfully');
          this.$emit('updated');
          this.$emit('close');
        } else {
          alert(res.data.message || 'Failed to report case');
        }
      } catch (e) {
        console.error("Failed to report case:", e);
        alert('Failed to report case to Woreda');
      } finally {
        this.reporting = false;
      }
    },

    addContact() {
      if (!this.newContact.name || !this.newContact.phone) {
        alert('Please enter contact name and phone number');
        return;
      }

      this.contacts.push({...this.newContact});

      // Reset form
      this.newContact = {
        name: '',
        phone: '',
        relationship: '',
        lastExposure: ''
      };
      this.showContactForm = false;

      // Here you would save to API
    },

    async saveNotes() {
      this.savingNotes = true;

      // Simulate API call
      setTimeout(() => {
        this.savingNotes = false;
        alert('Notes saved successfully');
      }, 1000);
    },

    updateCase() {
      this.$emit('close');
      // The parent component should open the update modal
    },

    printCase() {
      window.print();
    },

    // Utility methods
    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 'Unknown';

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        return age - 1;
      }

      return age + ' years';
    },

    getSeverityBadge(severity) {
      const badges = {
        'mild': 'badge bg-success',
        'moderate': 'badge bg-warning',
        'severe': 'badge bg-danger'
      };
      return badges[severity] || 'badge bg-secondary';
    },

    getTimelineClass(type) {
      const classes = {
        'created': 'bg-primary',
        'treatment': 'bg-success',
        'reported': 'bg-warning',
        'updated': 'bg-info'
      };
      return classes[type] || 'bg-secondary';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getDaysAgo(dateString) {
      if (!dateString) return 0;
      const date = new Date(dateString);
      const today = new Date();
      const diffTime = Math.abs(today - date);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 9px;
  top: 5px;
  bottom: 5px;
  width: 2px;
  background: #e9ecef;
}

.timeline-item {
  position: relative;
  padding-bottom: 20px;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-point {
  position: absolute;
  left: -21px;
  top: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 4px solid #fff;
  box-shadow: 0 0 0 1px #e9ecef;
}

.timeline-content {
  background: #f8f9fa;
  padding: 10px 15px;
  border-radius: 5px;
}

@media print {
  .modal-footer,
  .btn,
  .alert-dismissible {
    display: none !important;
  }

  .modal {
    position: static;
    background-color: transparent;
  }

  .modal-dialog {
    max-width: 100%;
    margin: 0;
  }

  .modal-content {
    border: none;
    box-shadow: none;
  }
}
</style>